import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { APIRequestPagination, APIRequestSort } from 'global';
import { MUITable } from 'components/shared/MUITable';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { GET_INVENTORY_OPTIONS } from '../../api';
import { InventoryOptionListResponse } from '../../types';
import { OptionsTableFilters } from './OptionsTableFilters';
import { createOptionsTableColumns, OptionTableEntity } from './tableConfig';

interface Props {
  accountId: string;
}

export const OptionsTable = ({ accountId }: Props) => {
  const history = useHistory();

  const form = useForm({ defaultValues: { search: '' } });
  const search = useWatch({ control: form.control, name: 'search' });

  const [pagination, setPagination] = useState<APIRequestPagination>({ page: 1, perPage: 15 });
  const [sorting, setSorting] = useState<APIRequestSort>({ key: 'code', value: 'asc' });

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [search]);

  const { data, loading, previousData } = useQuery<{ inventoryOptions: InventoryOptionListResponse }>(
    GET_INVENTORY_OPTIONS,
    {
      variables: { input: { accountId, pagination, sort: sorting, search } },
      fetchPolicy: 'cache-and-network',
    }
  );

  const openOption = (inventoryOptionId: string) => history.push(`/inventory/options/${inventoryOptionId}`);

  const columns = createOptionsTableColumns({ onEdit: openOption });

  const tableData: OptionTableEntity[] = ((data ?? previousData)?.inventoryOptions?.data ?? []).map(option => ({
    id: option.inventoryOptionId,
    inventoryOptionId: option.inventoryOptionId,
    code: option.code,
    name: option.name,
    description: option.description ?? '',
    pricingCount: option.pricing?.length ?? 0,
  }));

  return (
    <FormProvider {...form}>
      <OptionsTableFilters />

      <Box sx={{ height: 'calc(100% - 7rem)' }}>
        <MUITable<OptionTableEntity>
          stickyHeader
          columns={columns}
          isLoading={loading}
          tableData={tableData}
          sorting={{ ...sorting, onChange: setSorting }}
          onRowClick={(_event, row) => openOption(row.inventoryOptionId)}
          pagination={{
            ...pagination,
            total: data?.inventoryOptions?.total ?? 0,
            onChange: nextValue => setPagination(prev => ({ ...prev, ...nextValue })),
          }}
        />
      </Box>
    </FormProvider>
  );
};
